import React, { Component } from 'react';
import css from './CreateCouponPopup.css';
import Popup from 'reactjs-popup';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { saveCoupon } from '../../../../custom-ducks/coupons.duck';
import { notifySuccess, notifyError } from '../../../../components/HotToast/HotToastProvider';
import { IconSpinner } from '../../../../components';
import { X, Tag, RefreshCw } from 'react-feather';
import { POPUP_OVERLAY_STYLE, popupContentStyle } from '../../../../components/PopupShell/reactjsPopupStyles';

// Ambiguous characters (0/O, 1/I/L) dropped from the generated code alphabet
// so a coach reading a code aloud, or a client re-typing one from a screenshot,
// can't confuse letters and digits the way the CLAUDE.md publishable-key
// warning calls out for Stripe keys — same failure mode, different surface.
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 8;

const generateCode = () => {
  let code = '';
  for (let i = 0; i < CODE_LENGTH; i++) {
    code += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)];
  }
  return code;
};

const initialFormState = {
  code: '',
  codeError: null,
  discountType: 'percent', // 'percent' | 'fixed'
  discountValue: '',
  discountValueError: null,
  appliesToAll: true,
  selectedListingIds: [],
  listingsError: null,
  expiryDate: '',
  maxRedemptions: '',
  creating: false,
};

class CreateCouponPopup extends Component {
  constructor(props) {
    super(props);
    this.state = { ...initialFormState };
  }

  resetState() {
    this.setState({ ...initialFormState });
  }

  onGenerateCode() {
    this.setState({ code: generateCode(), codeError: null });
  }

  onToggleListing(listingId) {
    this.setState(prev => {
      const isSelected = prev.selectedListingIds.includes(listingId);
      return {
        selectedListingIds: isSelected
          ? prev.selectedListingIds.filter(id => id !== listingId)
          : [...prev.selectedListingIds, listingId],
        listingsError: null,
      };
    });
  }

  onCreate(close) {
    const { onSaveCoupon } = this.props;
    const code = (this.state.code || '').trim().toUpperCase();
    const discountValue = parseFloat(this.state.discountValue);
    const { discountType, appliesToAll, selectedListingIds } = this.state;

    let hasError = false;
    if (!code || !/^[A-Z0-9]+$/.test(code)) {
      this.setState({ codeError: 'Enter a code using only letters and numbers' });
      hasError = true;
    }
    if (!discountValue || discountValue <= 0) {
      this.setState({ discountValueError: 'Enter a discount amount greater than 0' });
      hasError = true;
    } else if (discountType === 'percent' && discountValue > 100) {
      this.setState({ discountValueError: 'A percentage discount cannot exceed 100%' });
      hasError = true;
    }
    if (!appliesToAll && selectedListingIds.length === 0) {
      this.setState({ listingsError: 'Select at least one ad, or apply the coupon to all your ads' });
      hasError = true;
    }
    if (hasError) return;

    this.setState({ creating: true });

    onSaveCoupon({
      code,
      discountType,
      discountValue,
      appliesToAll,
      listingIds: appliesToAll ? [] : selectedListingIds,
      expiryDate: this.state.expiryDate || null,
      maxRedemptions: this.state.maxRedemptions ? parseInt(this.state.maxRedemptions, 10) : null,
    })
      .then(() => {
        notifySuccess(`Coupon ${code} created.`);
        this.resetState();
        close();
        if (this.props.onCreated) this.props.onCreated();
      })
      .catch(err => {
        this.setState({ creating: false });
        const status = err && err.response && err.response.status;
        const errorCode = err && err.response && err.response.data && err.response.data.error;
        if (status === 409 && errorCode === 'code-already-exists') {
          this.setState({ codeError: 'You already have a coupon with this code' });
          return;
        }
        notifyError('Could not create the coupon. Please try again.');
      });
  }

  render() {
    const { trigger, listings } = this.props;
    const {
      code,
      codeError,
      discountType,
      discountValue,
      discountValueError,
      appliesToAll,
      selectedListingIds,
      listingsError,
      expiryDate,
      maxRedemptions,
      creating,
    } = this.state;

    const ownListings = listings || [];

    return (
      <Popup
        trigger={trigger}
        contentStyle={popupContentStyle({ width: '480px', maxWidth: '92vw' })}
        overlayStyle={POPUP_OVERLAY_STYLE}
        modal
        closeOnDocumentClick={false}
        lockScroll
        onClose={() => this.resetState()}
      >
        {close => (
          <div className={css.base}>
            <div className={css.header}>
              <button type="button" onClick={close} className={css.closeBtn} aria-label="Close">
                <X size={18} strokeWidth={2.5} />
              </button>

              <div className={css.iconBadge}>
                <Tag size={22} />
              </div>

              <p className={css.title}>Create Coupon</p>
              <p className={css.subTitle}>
                Give clients a discount code they can apply at checkout on one of your ads,
                or across all of them.
              </p>
            </div>
            <div className={css.body}>
              <label className={css.fieldLabel}>
                Coupon code <span className={css.requiredMark}>*</span>
              </label>
              <div className={css.codeRow}>
                <input
                  className={`${css.input} ${codeError ? css.inputError : ''}`}
                  type="text"
                  placeholder="e.g. WELCOME20"
                  value={code}
                  maxLength={20}
                  onChange={e =>
                    this.setState({ code: e.target.value.toUpperCase(), codeError: null })
                  }
                />
                <button
                  type="button"
                  className={css.generateBtn}
                  onClick={() => this.onGenerateCode()}
                  title="Generate a random code"
                >
                  <RefreshCw size={14} />
                  Generate
                </button>
              </div>
              {codeError && <span className={css.errorText}>{codeError}</span>}

              <div className={css.fieldRow}>
                <div className={css.fieldCol}>
                  <label className={css.fieldLabel}>Discount type</label>
                  <div className={css.segmented}>
                    <button
                      type="button"
                      className={discountType === 'percent' ? css.segmentActive : css.segment}
                      onClick={() => this.setState({ discountType: 'percent', discountValueError: null })}
                    >
                      % Percentage
                    </button>
                    <button
                      type="button"
                      className={discountType === 'fixed' ? css.segmentActive : css.segment}
                      onClick={() => this.setState({ discountType: 'fixed', discountValueError: null })}
                    >
                      $ Fixed amount
                    </button>
                  </div>
                </div>
                <div className={css.fieldCol}>
                  <label className={css.fieldLabel}>
                    Value <span className={css.requiredMark}>*</span>
                  </label>
                  <input
                    className={`${css.input} ${discountValueError ? css.inputError : ''}`}
                    type="number"
                    min={0}
                    max={discountType === 'percent' ? 100 : undefined}
                    placeholder={discountType === 'percent' ? '20' : '10'}
                    value={discountValue}
                    onChange={e =>
                      this.setState({ discountValue: e.target.value, discountValueError: null })
                    }
                  />
                </div>
              </div>
              {discountValueError && <span className={css.errorText}>{discountValueError}</span>}

              <label className={css.checkboxRow}>
                <input
                  type="checkbox"
                  checked={appliesToAll}
                  onChange={e => this.setState({ appliesToAll: e.target.checked, listingsError: null })}
                />
                Apply to all my active ads
              </label>

              {!appliesToAll && (
                <div className={css.listingPicker}>
                  {ownListings.length === 0 ? (
                    <p className={css.emptyListings}>You don't have any ads yet.</p>
                  ) : (
                    ownListings.map(item => (
                      <label key={item.id.uuid} className={css.listingRow}>
                        <input
                          type="checkbox"
                          checked={selectedListingIds.includes(item.id.uuid)}
                          onChange={() => this.onToggleListing(item.id.uuid)}
                        />
                        {item.attributes.title}
                      </label>
                    ))
                  )}
                  {listingsError && <span className={css.errorText}>{listingsError}</span>}
                </div>
              )}

              <div className={css.fieldRow}>
                <div className={css.fieldCol}>
                  <label className={css.fieldLabel}>Expiry date</label>
                  <input
                    className={css.input}
                    type="date"
                    value={expiryDate}
                    onChange={e => this.setState({ expiryDate: e.target.value })}
                  />
                </div>
                <div className={css.fieldCol}>
                  <label className={css.fieldLabel}>Max redemptions</label>
                  <input
                    className={css.input}
                    type="number"
                    min={1}
                    placeholder="Unlimited"
                    value={maxRedemptions}
                    onChange={e => this.setState({ maxRedemptions: e.target.value })}
                  />
                </div>
              </div>

              <div className={css.actions}>
                <button
                  type="button"
                  onClick={() => {
                    this.resetState();
                    close();
                  }}
                  className={css.cancelButton}
                >
                  Cancel
                </button>
                {creating ? (
                  <div className={css.creatingIndicator}>
                    <IconSpinner />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => this.onCreate(close)}
                    className={css.filledButton}
                  >
                    <Tag size={15} />
                    Create Coupon
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </Popup>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  onSaveCoupon: data => dispatch(saveCoupon(data)),
});

export default compose(connect(null, mapDispatchToProps))(CreateCouponPopup);
